/*
 * AgroPanel — cliente local compatível com o subconjunto do Supabase
 * utilizado pelo frontend original.
 *
 * DEV: /api -> API local AgroPanel
 */

const API_BASE = '/api';

type ApiError = {
  message: string;
};

type QueryResult<T = any> = {
  data: T | null;
  error: ApiError | null;
};

type AuthUser = {
  id: string;
  email?: string;
  [key: string]: any;
};

type AuthSession = {
  user: AuthUser;
  [key: string]: any;
};

type AuthCallback = (
  event: string,
  session: AuthSession | null
) => void;

const authListeners = new Set<AuthCallback>();

function normalizeError(error: unknown): ApiError {
  if (error instanceof Error) {
    return { message: error.message };
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error
  ) {
    return { message: String((error as any).message) };
  }

  return { message: String(error || 'Erro desconhecido') };
}

async function parseResponse(response: Response): Promise<any> {
  const text = await response.text();

  let body: any = null;

  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!response.ok) {
    const message =
      body?.error ||
      body?.message ||
      `Erro HTTP ${response.status}`;

    throw new Error(String(message));
  }

  return body;
}

function emitAuth(
  event: string,
  session: AuthSession | null
) {
  for (const callback of authListeners) {
    try {
      callback(event, session);
    } catch (error) {
      console.error('Erro em listener de autenticação:', error);
    }
  }
}

function tableEndpoint(
  table: string,
  method: string = 'GET'
) {
  if (
    method === 'GET' &&
    table === 'lancamentos_talhoes'
  ) {
    return `${API_BASE}/lancamentos-talhoes`;
  }

  return `${API_BASE}/${encodeURIComponent(table)}`;
}

type Filter = {
  type: 'eq' | 'gte' | 'lte';
  column: string;
  value: any;
};

class LocalQueryBuilder implements PromiseLike<QueryResult<any>> {
  private table: string;
  private method: 'GET' | 'POST' | 'PATCH' | 'DELETE' = 'GET';
  private payload: any = undefined;
  private filters: Filter[] = [];
  private orderColumn: string | null = null;
  private ascending = true;
  private limitValue: number | null = null;
  private selectValue = '*';
  private wantsSingle = false;
  constructor(table: string) {
    this.table = table;
  }

  select(columns = '*') {
    this.selectValue = columns;

    if (
      this.method === 'POST' ||
      this.method === 'PATCH'
    ) {
    }

    return this;
  }

  insert(payload: any) {
    this.method = 'POST';
    this.payload = payload;
    return this;
  }

  update(payload: any) {
    this.method = 'PATCH';
    this.payload = payload;
    return this;
  }

  delete() {
    this.method = 'DELETE';
    return this;
  }

  eq(column: string, value: any) {
    this.filters.push({
      type: 'eq',
      column,
      value,
    });
    return this;
  }

  gte(column: string, value: any) {
    this.filters.push({
      type: 'gte',
      column,
      value,
    });
    return this;
  }

  lte(column: string, value: any) {
    this.filters.push({
      type: 'lte',
      column,
      value,
    });
    return this;
  }

  order(
    column: string,
    options?: { ascending?: boolean }
  ) {
    this.orderColumn = column;
    this.ascending = options?.ascending !== false;
    return this;
  }

  limit(value: number) {
    this.limitValue = value;
    return this;
  }

  single() {
    this.wantsSingle = true;
    return this.execute();
  }

  private buildUrl() {
    const params = new URLSearchParams();

    if (this.selectValue) {
      params.set('select', this.selectValue);
    }

    for (const filter of this.filters) {
      if (filter.type === 'eq') {
        params.set(filter.column, String(filter.value));
      } else if (filter.type === 'gte') {
        params.set(
          `gte_${filter.column}`,
          String(filter.value)
        );
      } else if (filter.type === 'lte') {
        params.set(
          `lte_${filter.column}`,
          String(filter.value)
        );
      }
    }

    if (this.orderColumn) {
      params.set('order', this.orderColumn);
      params.set('asc', this.ascending ? 'true' : 'false');
    }

    if (this.limitValue !== null) {
      params.set('limit', String(this.limitValue));
    }



    const qs = params.toString();

    return qs
      ? `${tableEndpoint(this.table, this.method)}?${qs}`
      : tableEndpoint(this.table, this.method);
  }

  private async execute(): Promise<QueryResult<any>> {
    try {
      const options: RequestInit = {
        method: this.method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      };

      if (
        this.method === 'POST' ||
        this.method === 'PATCH'
      ) {
        options.body = JSON.stringify(this.payload);
      }

      const response = await fetch(
        this.buildUrl(),
        options
      );

      let data = await parseResponse(response);

      /*
       * Supabase .single():
       * recebe array da representação e entrega somente um objeto.
       */
      if (this.wantsSingle) {
        if (Array.isArray(data)) {
          if (data.length !== 1) {
            throw new Error(
              data.length === 0
                ? 'Nenhum registro encontrado'
                : 'Mais de um registro encontrado'
            );
          }

          data = data[0];
        }

        if (data === null || data === undefined) {
          throw new Error('Nenhum registro encontrado');
        }
      }

      return {
        data,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: normalizeError(error),
      };
    }
  }

  then<TResult1 = QueryResult<any>, TResult2 = never>(
    onfulfilled?:
      | ((value: QueryResult<any>) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?:
      | ((reason: any) => TResult2 | PromiseLike<TResult2>)
      | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

const auth = {
  async signInWithPassword({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<QueryResult<{ user: AuthUser; session: AuthSession }>> {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const body = await parseResponse(response);

      const session: AuthSession = body?.session || {
        user: body?.user,
      };

      if (!session?.user) {
        throw new Error('Invalid login credentials');
      }

      emitAuth('SIGNED_IN', session);

      return {
        data: {
          user: session.user,
          session,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: normalizeError(error),
      };
    }
  },

  async getSession(): Promise<{
    data: { session: AuthSession | null };
    error: ApiError | null;
  }> {
    try {
      const response = await fetch(`${API_BASE}/auth/session`, {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (response.status === 401) {
        return {
          data: { session: null },
          error: null,
        };
      }

      const body = await parseResponse(response);

      return {
        data: {
          session: body?.session || null,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: { session: null },
        error: normalizeError(error),
      };
    }
  },

  async signOut(): Promise<{
    error: ApiError | null;
  }> {
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'same-origin',
      });

      await parseResponse(response);

      emitAuth('SIGNED_OUT', null);

      return {
        error: null,
      };
    } catch (error) {
      /*
       * Mesmo se a API estiver indisponível, a interface precisa
       * abandonar a sessão visualmente.
       */
      emitAuth('SIGNED_OUT', null);

      return {
        error: normalizeError(error),
      };
    }
  },

  onAuthStateChange(callback: AuthCallback) {
    authListeners.add(callback);

    return {
      data: {
        subscription: {
          unsubscribe() {
            authListeners.delete(callback);
          },
        },
      },
    };
  },
};

export const supabase = {
  auth,

  from(table: string) {
    return new LocalQueryBuilder(table);
  },

  async rpc(
    functionName: string,
    args: Record<string, any> = {}
  ): Promise<QueryResult<any>> {
    try {
      const response = await fetch(
        `${API_BASE}/rpc/${encodeURIComponent(functionName)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(args),
        }
      );

      const data = await parseResponse(response);

      return {
        data,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: normalizeError(error),
      };
    }
  },
};

export const getSupabaseClient = () => supabase;

/*
 * Mantemos os tipos usados pelo frontend original.
 * Isso evita modificar páginas e componentes durante a migração.
 */
export type Database = {
  public: {
    Tables: {
      categorias: {
        Row: {
          id: string;
          nome: string;
          tipo: string;
          cor: string | null;
          icone: string | null;
          tipo_sugerido: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['categorias']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['categorias']['Insert']
        >;
      };

      talhoes: {
        Row: {
          id: string;
          nome: string;
          area_hectares: number | null;
          quantidade_pes: number;
          variedade: string | null;
          data_plantio: string | null;
          status: string | null;
          observacoes: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['talhoes']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['talhoes']['Insert']
        >;
      };

      colonos: {
        Row: {
          id: string;
          nome: string;
          contato: string | null;
          percentual: number;
          status: string | null;
          saldo: number | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['colonos']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['colonos']['Insert']
        >;
      };

      operacoes: {
        Row: {
          id: string;
          nome: string;
          tipo: string;
          icone: string | null;
          cor: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['operacoes']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['operacoes']['Insert']
        >;
      };

      viagens: {
        Row: {
          id: string;
          colono_id: string;
          data: string;
          caixas: number;
          kilos: number;
          valor_kilo: number;
          valor_pago: number;
          observacao: string | null;
          lancamento_id: string | null;
          created_at: string | null;
          updated_at: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['viagens']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['viagens']['Insert']
        >;
      };

      lancamentos: {
        Row: {
          id: string;
          data: string;
          tipo: string;
          categoria_id: string | null;
          descricao: string;
          valor_bruto: number;
          valor_liquido: number;
          colono_id: string | null;
          talhao_id: string | null;
          operacao_id: string | null;
          status: string | null;
          observacoes: string | null;
          created_at: string | null;
          updated_at: string | null;

          /*
           * Campos já existentes na aplicação/banco e usados
           * pelas telas de colonos/viagens.
           */
          caixas?: number | null;
          kilos?: number | null;
          valor_kilo?: number | null;
          viagem_id?: string | null;
        };
        Insert: Omit<
          Database['public']['Tables']['lancamentos']['Row'],
          'id' | 'created_at' | 'updated_at'
        >;
        Update: Partial<
          Database['public']['Tables']['lancamentos']['Insert']
        >;
      };
    };
  };
};
